import NoRecordsIcon from '@assets/icons/no-records-datatable.svg';
import { Text } from '@components/v2/ui';
import { Pagination } from '@components/v2/ui/Pagination';
import { ProjectCardSkeleton } from '@components/v2/ui/ProjectCard/ProjectCardSkeleton';
import { NOT_APPLICABLE } from '@config/constants/common';
import {
	ActionIcon,
	Badge,
	Box,
	Flex,
	Image,
} from '@mantine/core';
import useStore from '@store/index';
import { IconFilter } from '@tabler/icons-react';
import { AnimatePresence, motion } from 'framer-motion';
import { useTranslation } from 'react-i18next';
import classes from '../CreateOffset.module.css';

import { useWhoAmI } from '@api/get-whoami';
import { AdditionalityOffsetProjectCard } from '@components/v2/ui/AdditionalityOffsetProjectCard';
import { AREC_ORGANIZATION_IDS } from '@config/constants/organizations';
import { useAdditinalProjects } from '@features/offsets/api/get-additional-projects';
import { useOffsetProjectAdditional } from '@features/offsets/hooks/useOffsetProjectAdditional';
import { EProjectTechnology } from '@global-types/api/enum';
import {
	useDisclosure,
	useMediaQuery,
} from '@mantine/hooks';
import { interpolate } from '@utils/functions';
import { isEmpty } from 'lodash';
import { useEffect, useState } from 'react';
import AdditionalitiesOffsetSummaryCart from '../../AdditionalitiesOffsetSummaryCart/AdditionalitiesOffsetSummaryCart';
import { AdditionalityPoolProjectCard } from '../../AdditionalityPoolProjectCard/AdditionalityPoolProjectCard';
import FundedPartProgressBar from '../../FundedPartProgressBar/FundedPartProgressBar';
import { Filter } from './Filter';

const Step3 = () => {
	const [
		openedFilters,
		{ open: openFilters, close: closeFilters },
	] = useDisclosure(false);
	const [filtersNbr, setFiltersNbr] = useState(0);
	const { t } = useTranslation();
	const { data: cachedUser } = useWhoAmI();

	const {
		offsetCreationDatas,
		setOffsetsAdditionalProjects,
		offsetProducedAddis,
		setOffsetAdditionalProjectsPage,
		setOffsetAdditionalProjectsPageSize,
		offsetAdditionalProjectsPage,
		offsetAdditionalProjectsPageSize,
		offsetAdditionalProjectsQueryParams,
		offsetAdditionalProjects,
		offsetAdditionalProjectsSelected,
		setOffsetAdditionalProjects,
		setOffsetStep3Error,
		setIsEnabledOffsetNestStep,
	} = useStore();

	const {
		data: offsetsAdditionalProjectsApi,
		isLoading,
		total: totalItems,
		totalPages,
		isRefetching,
		isFetched,
	} = useAdditinalProjects(
		offsetAdditionalProjectsPage,
		offsetAdditionalProjectsPageSize,
		offsetAdditionalProjectsQueryParams
	);

	useEffect(() => {
		if (
			isFetched &&
			!isRefetching &&
			offsetsAdditionalProjectsApi
		) {
			setOffsetAdditionalProjects(
				offsetsAdditionalProjectsApi
			);
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [isFetched]);

	useEffect(() => {
		if (isRefetching && offsetsAdditionalProjectsApi) {
			const _cloneOffsetAdditionalityProjects =
				JSON.parse(
					JSON.stringify(
						offsetsAdditionalProjectsApi,
						null,
						2
					)
				);
			const _cloneOffsetAdditionalProjectsSelected =
				JSON.parse(
					JSON.stringify(
						offsetAdditionalProjectsSelected,
						null,
						2
					)
				);

			const newOffsetAdditionalProjects =
				_cloneOffsetAdditionalityProjects.map(
					(objet1: any) => {
						const objet2Correspondant =
							_cloneOffsetAdditionalProjectsSelected.find(
								(objet2: any) => objet2.id === objet1.id
							);
						if (objet2Correspondant) {
							return {
								...objet1,
								price: objet2Correspondant.price,
							};
						} else {
							return objet1;
						}
					}
				);

			setOffsetAdditionalProjects(
				newOffsetAdditionalProjects
			);
		}
	}, [isRefetching]);

	const {
		isSelectedProjectAdditional,
		handleRemoveSelectedProject,
		handleAddProject,
		handleOnChangePrice,
	} = useOffsetProjectAdditional();

	const totalPriceSelected =
		offsetAdditionalProjectsSelected.reduce(
			(sum, item) => sum + item.price,
			0
		);

	const remainingPrice =
		offsetProducedAddis - totalPriceSelected;

	const isMobile = useMediaQuery('(max-width: 868px)');

	return (
		<Box mt={'48px'}>
			<Filter
				opened={openedFilters}
				onClose={closeFilters}
				title={t('filters.titles.projects')}
				onApplyFilters={(filters) => {
					setFiltersNbr(Object.keys(filters).length);
				}}
				value={offsetAdditionalProjectsQueryParams}
			/>
			<Flex
				direction={isMobile ? 'column-reverse' : 'row'}
				gap={'24px'}
			>
				<Box w={'100%'}>
					<Flex
						justify={'space-between'}
						align={'center'}
						mb={'xs'}
					>
						<FundedPartProgressBar
							total={offsetProducedAddis}
							allocated={totalPriceSelected}
						/>
						<div style={{ position: 'relative' }}>
							{Object.keys(
								offsetAdditionalProjectsQueryParams
							).length > 0 && (
								<Badge
									style={{
										position: 'absolute',
										zIndex: '3',
										right: '-3px',
										fontFamily: 'InterDisplay-Regular',
									}}
									color="var(--mantine-color-igluRed-9)"
									size="xs"
									circle
									bg={
										!isEmpty(
											offsetAdditionalProjectsQueryParams
										)
											? 'var(--mantine-color-igluRed-9)'
											: 'transparent'
									}
								>
									{
										Object.keys(
											offsetAdditionalProjectsQueryParams
										).length
									}
								</Badge>
							)}

							<ActionIcon
								variant="outline"
								size="48px"
								radius="xl"
								data-testid="bell-icon-button"
								bg={
									!isEmpty(
										offsetAdditionalProjectsQueryParams
									)
										? 'var(--mantine-color-igluPrimary-9)'
										: 'transparent'
								}
								onClick={openFilters}
							>
								<IconFilter
									radius="xl"
									size={24}
									stroke={1.5}
									color={
										!isEmpty(
											offsetAdditionalProjectsQueryParams
										)
											? 'var(--mantine-color-igluTextButton-9)'
											: 'var(--mantine-color-igluPrimary-9)'
									}
								/>
							</ActionIcon>
						</div>
					</Flex>
					<Box
						style={{
							display: 'grid',
							gridTemplateColumns:
								'repeat(auto-fit, minmax(300px, 1fr))',
							maxWidth: '100%',
							gap: 'var(--mantine-spacing-sm)',
						}}
						mb={'var(--mantine-spacing-sm)'}
					>
						{Array.isArray(offsetAdditionalProjects) &&
							offsetAdditionalProjects.length > 0 &&
							offsetAdditionalProjects
								.filter(
									(project: any) =>
										project?.technology ==
										EProjectTechnology.PROJECT_FOUNDER
								)
								.map((project: any, index) => {
									const remainingQuantity =
										offsetProducedAddis -
										offsetAdditionalProjectsSelected.reduce(
											(acc, item) =>
												acc + (Number(item.price) || 0),
											0
										);

									const isAlreadySelected =
										offsetAdditionalProjectsSelected.some(
											(p) => p.id === project.id
										);

									const disableNumberInput =
										!isAlreadySelected &&
										(remainingQuantity === 0 ||
											offsetAdditionalProjectsSelected.length >=
												4);
									return (
										<AnimatePresence
											key={index}
											mode="wait"
										>
											<motion.div
												key={'empty'}
												initial={{ y: 10, opacity: 0 }}
												animate={{ y: 0, opacity: 1 }}
												exit={{ y: -10, opacity: 0 }}
												transition={{ duration: 0.2 }}
												style={{
													gridColumn:
														offsetAdditionalProjects.filter(
															(project: any) =>
																project?.technology ==
																EProjectTechnology.PROJECT_FOUNDER
														).length %
															2 !==
															0 &&
														index ===
															offsetAdditionalProjects.filter(
																(project: any) =>
																	project?.technology ==
																	EProjectTechnology.PROJECT_FOUNDER
															).length -
																1
															? '1 / -1'
															: 'auto',
													height: '100%',
												}}
											>
												<AdditionalityPoolProjectCard
													isArec={Boolean(
														cachedUser?.organization &&
														Object.values(
															AREC_ORGANIZATION_IDS
														).includes(
															cachedUser.organization
														)
													)}
													title={project.name}
													cover={project.image}
													description={interpolate(
														t(
															'global.additionalityPoolDescription'
														),
														{
															nom_project: project.name,
															organization:
																project.organization,
														}
													)}
													financing={{
														unlimited:
															project?.financing
																?.unlimited,
														pledged_euro:
															project?.financing
																?.pledged_euro,
														target_euro:
															project?.financing
																?.target_euro,
													}}
													onLearnMore={() => {}}
													onChangePrice={(val) => {
														handleOnChangePrice(
															project,
															val
														);
													}}
													fullWidth={true}
													isAdded={isSelectedProjectAdditional(
														project.id,
														offsetAdditionalProjectsSelected
													)}
													disabled={false}
													disableNumberInput={
														disableNumberInput
													}
													remainingQuantity={
														remainingPrice
													}
													price={project?.price || 0}
												/>
											</motion.div>
										</AnimatePresence>
									);
								})}
					</Box>
					<Box
						style={{
							display: 'grid',
							gridTemplateColumns:
								'repeat(auto-fill, minmax(300px, 1fr))',
							gap: 'var(--mantine-spacing-sm)',
						}}
					>
						{isLoading
							? [...Array(10).keys()].map(
									(project, index) => (
										<AnimatePresence
											key={index}
											mode="wait"
										>
											<motion.div
												key={'empty'}
												initial={{
													y: 10,
													opacity: 0,
												}}
												animate={{
													y: 0,
													opacity: 1,
												}}
												exit={{
													y: -10,
													opacity: 0,
												}}
												transition={{
													duration: 0.2,
												}}
												style={{
													display: 'flex',
													flex: 1,
												}}
											>
												<ProjectCardSkeleton
													fullWidth={true}
												/>
											</motion.div>
										</AnimatePresence>
									)
								)
							: !isLoading &&
								offsetAdditionalProjects.length > 0 &&
								offsetAdditionalProjects
									.filter(
										(project: any) =>
											project?.technology !==
											EProjectTechnology.PROJECT_FOUNDER
									)
									.map((project: any) => {
										const remainingQuantity =
											offsetProducedAddis -
											offsetAdditionalProjectsSelected.reduce(
												(acc, item) =>
													acc + (Number(item.price) || 0),
												0
											);

										const isAlreadySelected =
											offsetAdditionalProjectsSelected.some(
												(p) => p.id === project.id
											);

										const disableNumberInput =
											!isAlreadySelected &&
											(remainingQuantity === 0 ||
												offsetAdditionalProjectsSelected.length >=
													4);
										return (
											<AnimatePresence
												key={project.id}
												mode="wait"
											>
												<motion.div
													key={project.id}
													initial={{
														y: 10,
														opacity: 0,
													}}
													animate={{
														y: 0,
														opacity: 1,
													}}
													exit={{
														y: -10,
														opacity: 0,
													}}
													transition={{
														duration: 0.2,
													}}
													style={{
														display: 'flex',
														flex: 1,
													}}
												>
													<AdditionalityOffsetProjectCard
														isAdded={isSelectedProjectAdditional(
															project.id,
															offsetAdditionalProjectsSelected
														)}
														disableNumberInput={
															disableNumberInput
														}
														onChangePrice={(val) => {
															handleOnChangePrice(
																project,
																val
															);
														}}
														onAdd={() => {
															handleAddProject(project);
														}}
														cover={project?.image}
														type="additionality"
														projectType={project?.type}
														name={project?.name}
														active={project?.active}
														fullWidth={true}
														supportedByLogo={
															project?.supportedBy
														}
														location={{
															countryCode:
																project?.country?.code ||
																NOT_APPLICABLE,
															place:
																project?.location
																	?.place ||
																NOT_APPLICABLE,
															countryName:
																project?.country?.name ||
																NOT_APPLICABLE,
														}}
														onStatusChange={async () => {}}
														availableStock={
															project?.availableStock
														}
														fundedPart={
															project?.financing
																?.pledged_euro || 0
														}
														onClick={() => {}}
														showStatus={false}
														readonly={true}
														disableButton={
															remainingPrice <= 0 &&
															!isAlreadySelected
														}
														remainingPrice={
															remainingPrice
														}
														price={project?.price}
													/>
												</motion.div>
											</AnimatePresence>
										);
									})}
					</Box>

					{!isLoading &&
						offsetAdditionalProjects.length == 0 && (
							<Box className={classes.containerEmptyList}>
								<Box
									className={
										classes.containerTextsNoRecords
									}
								>
									<Image
										src={NoRecordsIcon}
										className={classes.iconNoRecords}
									/>
									<>
										<Text
											className={classes.titleNoRecords}
											variant="subtitle1"
											c="var(--mantine-color-igluPrimary-9)"
										>
											{t(
												'global.datatable.noResultsFound'
											)}
										</Text>
										<Text
											className={
												classes.descriptionNoRecords
											}
											variant="body2"
										>
											{t(
												'global.datatable.noResultsFoundDescription'
											)}
										</Text>
									</>
								</Box>
							</Box>
						)}
				</Box>

				<AdditionalitiesOffsetSummaryCart
					items={offsetAdditionalProjectsSelected}
					total={totalPriceSelected}
					onRemove={handleRemoveSelectedProject}
				/>
			</Flex>
			<Box mt={'24px'}>
				<Pagination
					pageSize={
						offsetAdditionalProjectsPageSize as number
					}
					totalItems={totalItems}
					activePage={offsetAdditionalProjectsPage}
					onPageSizeChange={function (
						_size: string | number
					) {
						setOffsetAdditionalProjectsPageSize(_size);
						setOffsetAdditionalProjectsPage(1);
					}}
					onChange={(data) => {
						setOffsetAdditionalProjectsPage(data);
					}}
					total={totalPages ? totalPages : 0}
				/>
			</Box>
		</Box>
	);
};

export default Step3;
